/**
 * API 请求工具层
 * 使用 @tencent/wetv-kernel 的 fetcher 进行请求
 * 参考 tv-linux 项目的实现
 */

import wetvKernel from '@tencent/wetv-kernel';
import { qua, beforeRequest, getAppid } from '../utils/apiUtils';
import {
  getCache,
  setCache,
  getPendingRequest,
  setPendingRequest,
  clearPendingRequest,
  CacheConfig,
} from '../utils/apiCache';
import { VERSION } from '@/config/version';

const { fetcher } = wetvKernel;
const { videoAppid } = getAppid();

// 本地维护的后端版本号
const backendVersion = 1;

/**
 * 构建版本号字符串
 * VERSION 格式: 3.0.0 或 3.0.1 (3.0.x)
 * 输出格式: 3.0.backendVersion.x (例如: 3.0.1.0 或 3.0.1.1)
 */
function buildVersion(): string {
  const versionParts = VERSION.split('.');
  const major = versionParts[0] || '3';
  const minor = versionParts[1] || '0';
  const patch = versionParts[2] || '0';
  return `${major}.${minor}.${backendVersion}.${patch}`;
}

// API 响应基础类型
export interface ApiResponse<T = any> {
  code?: number;
  ret?: number;
  errCode?: number;
  err_code?: number;
  message?: string;
  data?: T;
  [key: string]: any;
}

// 请求配置类型
export interface RequestConfig {
  CSRF?: boolean;
  originReturn?: boolean;
  withCredentials?: boolean;
  params?: Record<string, string | number | boolean>;
  /** 缓存配置 */
  cache?: CacheConfig | boolean;
}

// API 错误类型
export class ApiError extends Error {
  code: number;
  data?: any;

  constructor(message: string, code = -1, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.data = data;
  }
}

/**
 * 处理响应数据
 */
function handleResponse<T>(response: any): ApiResponse<T> {
  // 检查响应是否为空或 null
  if (!response || typeof response !== 'object') {
    throw new ApiError('Invalid response format', -1, response);
  }

  // 检查是否有错误
  if (response.ret !== undefined && response.ret !== 0) {
    throw new ApiError(response.msg || response.message || 'Request failed', response.ret, response.data);
  }
  if (response.errCode !== undefined && response.errCode !== 0) {
    throw new ApiError(response.errMsg || response.message || 'Request failed', response.errCode, response.data);
  }
  if (response.err_code !== undefined && response.err_code !== 0) {
    throw new ApiError(response.err_msg || response.message || 'Request failed', response.err_code, response.data);
  }
  if (response.code !== undefined && response.code !== 0 && response.code !== 200) {
    throw new ApiError(response.message || 'Request failed', response.code, response.data);
  }

  return response as ApiResponse<T>;
}

/**
 * POST 请求（使用 fetcher.post）
 */
export async function post<T = any>(path: string, body?: any, config: RequestConfig = {}): Promise<ApiResponse<T>> {
  beforeRequest();
  // 构建基础公参
  const version = buildVersion();
  const baseParams = {
    vversion_name: version,
  };
  const { params = {}, originReturn = false, CSRF = true, cache: cacheConfig, ...restConfig } = config;

  // 处理缓存配置：如果传入 boolean，转换为 CacheConfig
  const finalCacheConfig = typeof cacheConfig === 'boolean' ? { enabled: cacheConfig } : cacheConfig;

  const url = fetcher.getPbAccessUri({ path });
  const method = 'POST';

  // 检查是否有正在进行的相同请求
  const pendingRequest = getPendingRequest<ApiResponse<T>>(method, url, params, body);
  if (pendingRequest) {
    return pendingRequest;
  }

  // 检查缓存
  const cachedItem = getCache<ApiResponse<T>>(method, url, params, body, finalCacheConfig);
  if (cachedItem?.data) {
    // 立即返回缓存数据（stale-while-revalidate）
    const cachedData = cachedItem.data;

    // 异步更新缓存（不阻塞返回）
    const updatePromise = (async () => {
      try {
        const response = await fetcher.post(url, body, {
          CSRF,
          originReturn,
          ...restConfig,
          params: {
            ...params,
            ...baseParams,
            'Q-UA': qua(),
            video_appid: videoAppid,
          },
        });

        const result = originReturn ? (response as ApiResponse<T>) : handleResponse<T>(response);

        // 更新缓存
        setCache(method, url, result, params, body, finalCacheConfig);
        clearPendingRequest(method, url, params, body);
        return result;
      } catch (error) {
        clearPendingRequest(method, url, params, body);
        // 更新失败不影响已返回的缓存数据
        throw error;
      }
    })();

    setPendingRequest(method, url, updatePromise, params, body, finalCacheConfig);

    return cachedData;
  }

  // 没有缓存，发起新请求
  try {
    const requestPromise = (async () => {
      const response = await fetcher.post(url, body, {
        CSRF,
        originReturn,
        ...restConfig,
        params: {
          ...params,
          ...baseParams,
          'Q-UA': qua(),
          video_appid: videoAppid,
        },
      });

      // 如果 originReturn 为 true，直接返回原始响应，不进行错误处理
      if (originReturn) {
        const result = response as ApiResponse<T>;
        setCache(method, url, result, params, body, finalCacheConfig);
        clearPendingRequest(method, url, params, body);
        return result;
      }

      const result = handleResponse<T>(response);
      setCache(method, url, result, params, body, finalCacheConfig);
      clearPendingRequest(method, url, params, body);
      return result;
    })();

    setPendingRequest(method, url, requestPromise, params, body, finalCacheConfig);
    return await requestPromise;
  } catch (error) {
    clearPendingRequest(method, url, params, body);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * GET 请求（使用 fetcher.get）
 */
export async function get<T = any>(url: string, params?: any, config: RequestConfig = {}): Promise<ApiResponse<T>> {
  beforeRequest();
  // 构建基础公参
  const baseParams = {
    vversion_name: buildVersion(),
  };
  const { params: configParams = {}, cache: cacheConfig, ...restConfig } = config;

  // 处理缓存配置：如果传入 boolean，转换为 CacheConfig
  const finalCacheConfig = typeof cacheConfig === 'boolean' ? { enabled: cacheConfig } : cacheConfig;

  const method = 'GET';
  const finalParams = {
    ...configParams,
    ...baseParams,
    ...(typeof params === 'object' ? params : {}),
    'Q-UA': qua(),
    video_appid: videoAppid,
  };

  // 检查是否有正在进行的相同请求
  const pendingRequest = getPendingRequest<ApiResponse<T>>(method, url, finalParams);
  if (pendingRequest) {
    return pendingRequest;
  }

  // 检查缓存
  const cachedItem = getCache<ApiResponse<T>>(method, url, finalParams, undefined, finalCacheConfig);
  if (cachedItem?.data) {
    // 立即返回缓存数据（stale-while-revalidate）
    const cachedData = cachedItem.data;

    // 异步更新缓存（不阻塞返回）
    const updatePromise = (async () => {
      try {
        const response = await fetcher.get(url, params, {
          CSRF: true,
          ...restConfig,
          params: finalParams,
        });

        const result = handleResponse<T>(response);
        // 更新缓存
        setCache(method, url, result, finalParams, undefined, finalCacheConfig);
        clearPendingRequest(method, url, finalParams);
        return result;
      } catch (error) {
        clearPendingRequest(method, url, finalParams);
        // 更新失败不影响已返回的缓存数据
        throw error;
      }
    })();

    setPendingRequest(method, url, updatePromise, finalParams, undefined, finalCacheConfig);

    return cachedData;
  }

  // 没有缓存，发起新请求
  try {
    const requestPromise = (async () => {
      const response = await fetcher.get(url, params, {
        CSRF: true,
        ...restConfig,
        params: finalParams,
      });

      const result = handleResponse<T>(response);
      setCache(method, url, result, finalParams, undefined, finalCacheConfig);
      clearPendingRequest(method, url, finalParams);
      return result;
    })();

    setPendingRequest(method, url, requestPromise, finalParams, undefined, finalCacheConfig);
    return await requestPromise;
  } catch (error) {
    clearPendingRequest(method, url, finalParams);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// 导出默认对象
export default {
  get,
  post,
};
